import { useEffect, useRef, useState, type ReactNode } from "react";

import { cx } from "../lib/cx";

export interface MenuItem {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

/**
 * A small popup menu: the one row-actions control the settings lists share.
 * Items are supplied already filtered by ability, so a menu with nothing in it
 * renders as a disabled button rather than an empty popup.
 */
export function Menu({
  buttonLabel,
  menuLabel,
  items,
  trigger = "Actions",
}: {
  /** Accessible name of the trigger, e.g. "Actions for Ada Lovelace". */
  buttonLabel: string;
  menuLabel: string;
  items: MenuItem[];
  trigger?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={buttonLabel}
        disabled={items.length === 0}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="rounded-md px-2 py-1 text-sm text-muted hover:bg-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
      >
        {trigger}
      </button>

      {open && (
        <ul
          role="menu"
          aria-label={menuLabel}
          className="app-shadow absolute right-0 z-30 mt-1 w-48 rounded-lg border border-line bg-raised py-1 text-sm"
        >
          {items.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled ?? false}
                className={cx(
                  "w-full px-3 py-2 text-left hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40",
                  item.destructive === true && "text-danger",
                )}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
